import './Calendar.scss';
import CalendarDay from './CalendarDay';
import CalendarMonth from './CalendarMonth';
import CalendarYear from './CalendarYear';
import { copyDate, dateToString } from '../../../utilities/calendar-utilities';
import React from 'react';
import CoreComponent from 'utilities/component-utilities/CoreComponent';

// #region model definitions
interface CalendarProps {
    className?: string,
    showWeekNumber?: boolean,
    minDate?: Date,
    maxDate?: Date,
    disabledDates?: Array<Date>,
    selectedDate?: Date,
    onDateSelected?: (date: Date, e: React.MouseEvent) => void,
    viewDate?: Date
    highlightDates?: Date[] | null,
    name: string,
}

type CalendarView = 'date' | 'month' | 'year';
interface IChangeCalendarView {
    month?: number,
    year?: number,
    view?: CalendarView
}

export type ChangeCalendarView = IChangeCalendarView | CalendarView;

interface CalendarState {
    viewMonth: number
    viewYear: number,
    selectedDate: Date | null,
    view: CalendarView,
    minDate: Date,
}
// #endregion model definitions

class Calendar extends CoreComponent<CalendarProps, CalendarState> {
    readonly today: Date;

    constructor(props: CalendarProps) {
        super(props);
        this.today = new Date();

        this.state = {
            viewMonth: props.viewDate?.getMonth() || props.minDate?.getMonth() || this.today.getMonth(),
            viewYear: props.viewDate?.getFullYear() || props.minDate?.getFullYear() || this.today.getFullYear(),
            selectedDate: null,
            view: 'date',
            minDate: new Date(1970, 1, 1)
        };
    }

    componentDidMount() {
        if (this.props.selectedDate) {
            this.setState({
                viewMonth: this.props.selectedDate.getMonth(),
                viewYear: this.props.selectedDate.getFullYear(),
                selectedDate: copyDate(this.props.selectedDate),
                minDate: new Date(1970, 1, 1)
            });
        }
    }

    componentDidUpdate(prevProps: CalendarProps, prevState: CalendarState) {
        if (prevProps.selectedDate?.getTime() !== this.props.selectedDate?.getTime()) {
            this.setState({
                viewMonth: this.props.selectedDate?.getMonth() || prevState.viewMonth,
                viewYear: this.props.selectedDate?.getFullYear() || prevState.viewYear,
                selectedDate: this.props.selectedDate ? copyDate(this.props.selectedDate) : null
            });
        }
    }

    // #region logic
    // #region onchange
    changeView = (view: ChangeCalendarView, e?: React.MouseEvent | WheelEvent) => {
        if(e) {
            e.stopPropagation();
            e.preventDefault();
        }
        if (typeof view === 'string') {
            this.setState({
                view: view
            });
        }
        else {
            this.setState({
                view: view.view || this.state.view,
                viewMonth: view.month || view.month === 0 ? view.month : this.state.viewMonth,
                viewYear: view.year || this.state.viewYear
            });
        }
    }


    private onDayClick = (date: Date, e: React.MouseEvent) => {
        if (this.isDisabledDate(date)) {
            return;
        }

        this.setState({
            selectedDate: copyDate(date),
            viewMonth: date.getMonth(),
            viewYear: date.getFullYear()
        });

        if (this.props.onDateSelected) {
            this.props.onDateSelected(date, e);
        }
    }

    // #endregion onchange

    private getMinDate = (): Date | undefined => {
        let minDate = this.props.minDate ? copyDate(this.props.minDate) : undefined;
        if (this.state.minDate) {
            if (!minDate || this.state.minDate > minDate) minDate = this.state.minDate;
        }

        return minDate;
    };

    private isDisabledDate = (date: Date): boolean => {
        if (this.props.minDate && dateToString(date) < dateToString(this.props.minDate)) {
            return true;
        }
        if (this.props.maxDate && dateToString(date) > dateToString(this.props.maxDate)) return true;
        if (this.state.minDate && dateToString(date) < dateToString(this.state.minDate)) return true;
        if (this.props.disabledDates?.some(d => dateToString(d) === dateToString(date))) return true;

        return false;
    }

    private isDisabledYear = (year: number) => {
        const minDate = this.getMinDate();
        if (!minDate) return false;
        if (minDate.getFullYear() > year) return true;
        if (this.props.maxDate) {
            if (this.props.maxDate.getFullYear() < year) return true;
        }
        return false;
    };

    private isDisabledMonth = (month: number, year: number) => {
        const minDate = this.getMinDate();
        if (!minDate) return false;
        if (this.isDisabledYear(year)) return true;
        if (minDate.getMonth() > month && minDate.getFullYear() === year) return true;
        if (this.props.maxDate) {
            if (this.props.maxDate.getMonth() < month && this.props.maxDate.getFullYear() === year) return true;
        }
        return false;
    };
    // #endregion logic

    // #region render

    renderBody = () => {
        switch (this.state.view) {
            case 'month':
                return <CalendarMonth
                    name={this.props.name + '-calendar-view'}
                    onChange={this.changeView}
                    value={this.state.viewMonth}
                    viewYear={this.state.viewYear}
                    isDisabled={this.isDisabledMonth}
                    highlightDates={this.props.highlightDates}
                />;
            case 'year':
                return <CalendarYear
                    name={this.props.name + '-calendar-view'}
                    onChange={this.changeView}
                    value={this.state.viewYear}
                    minDate={this.getMinDate()}
                    maxDate={this.props.maxDate}
                    isDisabled={this.isDisabledYear}
                    highlightDates={this.props.highlightDates}
                />;
            case 'date':
            default:
                return <CalendarDay
                    name={this.props.name + '-calendar-view'}
                    onChange={this.onDayClick}
                    value={this.state.selectedDate}
                    viewYear={this.state.viewYear}
                    viewMonth={this.state.viewMonth}
                    isDisabledDate={this.isDisabledDate}
                    showWeekNumber={this.props.showWeekNumber}
                    changeView={this.changeView}
                    highlightDates={this.props.highlightDates}
                />;
        }
    }

    render() {
        return (
            <div className={`app-calendar ${this.props.className || ''}`}>
                <div className='app-calendar-wrapper shadow shadow-black dark:shadow-neutral-900'>
                    {this.renderBody()}
                </div>
            </div>
        );
    }
    // #endregion render
}
export default Calendar;
